import { defineStore } from "pinia"
import { computed, ref } from "vue"

/**
 * Holds both user and admin tokens independently.
 * Same browser can be signed in as a user AND an admin — useful for dev
 * and also legitimate (admins are distinct identities from regular users).
 */
export const useAuthStore = defineStore("auth", () => {
  const userToken = ref<string | null>(null)
  const userUsername = ref<string | null>(null)

  const adminToken = ref<string | null>(null)
  const adminUsername = ref<string | null>(null)
  const adminRole = ref<string | null>(null)

  const isUser = computed(() => !!userToken.value)
  const isAdmin = computed(() => !!adminToken.value)

  function restore() {
    userToken.value = localStorage.getItem("user_token")
    userUsername.value = localStorage.getItem("user_username")
    adminToken.value = localStorage.getItem("admin_token")
    adminUsername.value = localStorage.getItem("admin_username")
    adminRole.value = localStorage.getItem("admin_role")
  }

  function setUser(token: string, username: string) {
    userToken.value = token
    userUsername.value = username
    localStorage.setItem("user_token", token)
    localStorage.setItem("user_username", username)
  }

  function clearUser() {
    userToken.value = null
    userUsername.value = null
    localStorage.removeItem("user_token")
    localStorage.removeItem("user_username")
  }

  function setAdmin(token: string, username: string, role: string) {
    adminToken.value = token
    adminUsername.value = username
    adminRole.value = role
    localStorage.setItem("admin_token", token)
    localStorage.setItem("admin_username", username)
    localStorage.setItem("admin_role", role)
  }

  function clearAdmin() {
    adminToken.value = null
    adminUsername.value = null
    adminRole.value = null
    localStorage.removeItem("admin_token")
    localStorage.removeItem("admin_username")
    localStorage.removeItem("admin_role")
  }

  return {
    userToken,
    userUsername,
    adminToken,
    adminUsername,
    adminRole,
    isUser,
    isAdmin,
    restore,
    setUser,
    clearUser,
    setAdmin,
    clearAdmin,
  }
})
